// -------------------------------------------------------------------------
// Sidebar item registry — the single source of truth on the frontend.
//
// The `key` field in each nav item MUST match one of the keys stored by the
// backend (`SIDEBAR_ITEM_KEYS` in server.py).  When the super admin customizes
// an org's layout, the backend returns an ordered array of `{key, visible}`
// and the sidebar component consumes it via `applyLayoutToItems` below.
//
// Items whose `locked: true` flag is set are always visible regardless of the
// stored layout — this prevents an admin from accidentally hiding critical
// navigation (Home/Dashboard and Settings) and locking themselves out.
// -------------------------------------------------------------------------

import { show_phi, phiText } from './phiConfig';

export const SIDEBAR_LOCKED_KEYS = new Set(['dashboard', 'settings']);

// Human-readable labels for the customization panel.  Keys must be a superset
// of every `key` used across role nav lists.
export const SIDEBAR_ITEM_LABELS = {
  dashboard:      'Dashboard',
  schedule:       phiText('Schedule', 'Task Calendar'),
  patients:       'Patients',
  map:            'Map',
  documents:      'Documents',
  team:           'Team',
  credentialing:  'Credentialing',
  kpi:            'KPI & Revenue',
  invoices:       'Invoices & A/R',
  payroll:        'Payroll',
  compliance:     'Compliance',
  security:       'Security',
  audit_logs:     'Audit Logs',
  settings:       'Settings',
};

// The canonical registry order — mirrors the default backend order and is
// used to seed the customization UI when no layout has been saved yet.
export const SIDEBAR_REGISTRY_KEYS = [
  'dashboard',
  'schedule',
  ...(show_phi ? ['patients'] : []),
  'map',
  'documents',
  'team',
  'credentialing',
  'kpi',
  'invoices',
  'payroll',
  'compliance',
  'security',
  'audit_logs',
  'settings',
];

/**
 * Apply a stored per-org layout to a role-specific list of sidebar items.
 *
 * @param {Array} items    Role-specific nav items (each with a `key` field).
 * @param {Array} layout   Backend-normalized array of {key, visible, order}.
 * @returns {Array}        Filtered + reordered items ready for render.
 *
 * Rules:
 *   1. Locked items (`item.locked` OR `SIDEBAR_LOCKED_KEYS`) are ALWAYS kept.
 *   2. Non-locked items whose `visible === false` in layout are dropped.
 *   3. Items are reordered by the layout's `order`.  Items missing from the
 *      layout (e.g. role-specific extras like 'my_profile' or 'vom_home')
 *      are appended at the end in their original relative order.
 *   4. When `layout` is empty/undefined, we return `items` unchanged so the
 *      default sidebar experience is preserved.
 */
export function applyLayoutToItems(items, layout) {
  if (!Array.isArray(layout) || layout.length === 0) return items;

  // Build a quick lookup: key → { visible, order }
  const layoutByKey = {};
  layout.forEach((entry) => {
    if (entry && typeof entry.key === 'string') {
      layoutByKey[entry.key] = {
        visible: entry.visible !== false,
        order: typeof entry.order === 'number' ? entry.order : 9999,
      };
    }
  });

  const inLayout = [];
  const notInLayout = [];
  items.forEach((item, originalIdx) => {
    const cfg = layoutByKey[item.key];
    const isLocked = !!item.locked || SIDEBAR_LOCKED_KEYS.has(item.key);
    if (cfg) {
      if (!isLocked && cfg.visible === false) return; // hidden by admin
      inLayout.push({ item, sortKey: cfg.order });
    } else {
      // No layout entry for this role-specific extra — keep at original spot.
      notInLayout.push({ item, sortKey: 100000 + originalIdx });
    }
  });

  const merged = [...inLayout, ...notInLayout];
  merged.sort((a, b) => a.sortKey - b.sortKey);
  return merged.map((m) => m.item);
}
